import { normalizeReviewText, isReviewHiddenReason, isReviewVisibility } from '../lib/reviews';
import type { Database, SupabaseClient } from '../supabase/client';
import type {
  AdminProductReviewRow,
  AdminReviewedProductOption,
  AdminReviewFilters,
  AdminReviewsPage,
  CustomerProductReview,
  CustomerProductReviewReceipt,
  UpdateCustomerProductReviewInput,
  UpdateCustomerProductReviewResult,
  PublicProductReviewSnapshot,
  PublicReviewCard,
  PublicStoreReviewSummary,
  ReviewErrorKey,
  ReviewVerificationSource,
  ReviewableOrderItem,
  RemoveReviewResponseInput,
  SetReviewVisibilityInput,
  SubmitProductReviewInput,
  SubmitProductReviewResult,
  UpsertReviewResponseInput,
} from '../types/review';

type ReviewableRpcRow =
  Database['public']['Functions']['list_customer_reviewable_order_items']['Returns'][number];
type SubmitReviewRpcRow =
  Database['public']['Functions']['submit_product_review']['Returns'][number];
type CustomerReviewRpcRow =
  Database['public']['Functions']['get_customer_product_review']['Returns'][number];
type AdminReviewRpcRow =
  Database['public']['Functions']['list_admin_product_reviews']['Returns'][number];
type AdminReviewedProductOptionRpcRow =
  Database['public']['Functions']['list_admin_reviewed_product_options']['Returns'][number];

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export interface ReviewService {
  listReviewableOrderItems(
    client: SupabaseClient,
    args: { readonly orderId?: string | null; readonly limit?: number },
  ): Promise<readonly ReviewableOrderItem[]>;
  submitProductReview(
    client: SupabaseClient,
    input: SubmitProductReviewInput,
  ): Promise<SubmitProductReviewResult>;
  getCustomerProductReview(
    client: SupabaseClient,
    args: { readonly storeSlug: string; readonly productId: string },
  ): Promise<CustomerProductReview | null>;
  updateCustomerProductReview(
    client: SupabaseClient,
    input: UpdateCustomerProductReviewInput,
  ): Promise<UpdateCustomerProductReviewResult>;
  getPublicProductReviewSnapshot(
    client: SupabaseClient,
    args: {
      readonly storeSlug: string;
      readonly productId: string;
      readonly limit?: number;
      readonly highlightedReviewId?: string | null;
    },
  ): Promise<PublicProductReviewSnapshot>;
  listAdminReviewedProductOptions(
    client: SupabaseClient,
    args: {
      readonly storeId: string;
      readonly search?: string | null;
      readonly limit?: number;
      readonly offset?: number;
    },
  ): Promise<readonly AdminReviewedProductOption[]>;
  listAdminProductReviews(
    client: SupabaseClient,
    filters: AdminReviewFilters,
  ): Promise<AdminReviewsPage>;
  upsertProductReviewResponse(
    client: SupabaseClient,
    input: UpsertReviewResponseInput,
  ): Promise<void>;
  removeProductReviewResponse(
    client: SupabaseClient,
    input: RemoveReviewResponseInput,
  ): Promise<void>;
  setProductReviewVisibility(
    client: SupabaseClient,
    input: SetReviewVisibilityInput,
  ): Promise<void>;
}

function normalizeLimit(value: number | undefined, fallback: number, max: number): number {
  return Math.min(
    Math.max(Number.isFinite(value ?? fallback) ? Math.trunc(value ?? fallback) : fallback, 1),
    max,
  );
}

function normalizeOffset(value: number | undefined): number {
  return Math.max(Number.isFinite(value ?? 0) ? Math.trunc(value ?? 0) : 0, 0);
}

function normalizeOptionalUuid(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim() ?? '';
  return UUID_RE.test(trimmed) ? trimmed : undefined;
}

function nullableString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function toReceipt(row: SubmitReviewRpcRow): CustomerProductReviewReceipt {
  return {
    id: row.id,
    store_id: row.store_id,
    product_id: row.product_id,
    variant_id: row.variant_id,
    order_item_id: row.order_item_id,
    rating: row.rating,
    review_text: nullableString(row.review_text),
    customer_display_name: nullableString(row.customer_display_name),
    verification_source: row.verification_source as ReviewVerificationSource,
    visibility: isReviewVisibility(row.visibility) ? row.visibility : 'visible',
    created_at: row.created_at,
  };
}

function toCustomerReview(row: CustomerReviewRpcRow): CustomerProductReview {
  return {
    ...toReceipt(row),
    order_id: row.order_id,
    merchant_response_text: nullableString(row.merchant_response_text),
    merchant_response_created_at: nullableString(row.merchant_response_created_at),
    updated_at: row.updated_at,
  };
}

function toReviewableOrderItem(row: ReviewableRpcRow): ReviewableOrderItem {
  const reviewId = nullableString(row.review_id);
  const reviewCreatedAt = nullableString(row.review_created_at);
  const reviewUpdatedAt = nullableString(row.review_updated_at) ?? reviewCreatedAt;
  const review =
    reviewId && typeof row.review_rating === 'number' && reviewCreatedAt && reviewUpdatedAt
      ? ({
          id: reviewId,
          store_id: row.store_id,
          product_id: row.product_id,
          variant_id: row.variant_id,
          order_id: row.order_id,
          order_item_id: row.order_item_id,
          rating: row.review_rating,
          review_text: nullableString(row.review_text),
          customer_display_name: nullableString(row.review_customer_display_name),
          verification_source: 'dukanti_order',
          visibility: isReviewVisibility(row.review_visibility) ? row.review_visibility : 'visible',
          merchant_response_text: nullableString(row.review_merchant_response_text),
          merchant_response_created_at: nullableString(row.review_merchant_response_created_at),
          created_at: reviewCreatedAt,
          updated_at: reviewUpdatedAt,
        } satisfies CustomerProductReview)
      : null;

  return {
    order_id: row.order_id,
    order_item_id: row.order_item_id,
    store_id: row.store_id,
    product_id: row.product_id,
    variant_id: row.variant_id,
    product_name_snapshot: row.product_name_snapshot,
    product_image_path_snapshot: nullableString(row.product_image_path_snapshot),
    variant_option_summary_snapshot: row.variant_option_summary_snapshot,
    quantity: row.quantity,
    delivered_at: row.delivered_at,
    already_reviewed: row.already_reviewed || review !== null,
    review,
  };
}

function toPublicReviewCard(value: unknown): PublicReviewCard | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  if (
    typeof row.id !== 'string' ||
    typeof row.product_id !== 'string' ||
    typeof row.rating !== 'number' ||
    typeof row.created_at !== 'string'
  ) {
    return null;
  }
  const card: PublicReviewCard = {
    id: row.id,
    product_id: row.product_id,
    rating: row.rating,
    review_text: nullableString(row.review_text),
    customer_display_name: nullableString(row.customer_display_name),
    created_at: row.created_at,
    merchant_response_text: nullableString(row.merchant_response_text),
    merchant_response_created_at: nullableString(row.merchant_response_created_at),
  };
  return row.is_highlighted === true ? { ...card, is_highlighted: true } : card;
}

export function parsePublicStoreReviewSummary(value: unknown): PublicStoreReviewSummary | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  const count = typeof row.review_count === 'number' ? row.review_count : 0;
  if (count <= 0) return null;
  return {
    average_rating: typeof row.average_rating === 'number' ? row.average_rating : null,
    review_count: count,
    reviews: Array.isArray(row.reviews)
      ? row.reviews.flatMap((item) => toPublicReviewCard(item) ?? [])
      : [],
  };
}

function toPublicProductReviewSnapshot(
  value: unknown,
  productId: string,
): PublicProductReviewSnapshot {
  if (!value || typeof value !== 'object') {
    return { product_id: productId, average_rating: null, review_count: 0, reviews: [] };
  }
  const row = value as Record<string, unknown>;
  const snapshot: PublicProductReviewSnapshot = {
    product_id: typeof row.product_id === 'string' ? row.product_id : productId,
    average_rating: typeof row.average_rating === 'number' ? row.average_rating : null,
    review_count: typeof row.review_count === 'number' ? row.review_count : 0,
    reviews: Array.isArray(row.reviews)
      ? row.reviews.flatMap((item) => toPublicReviewCard(item) ?? [])
      : [],
  };
  const highlightedReviewId = nullableString(row.highlighted_review_id);
  return highlightedReviewId && snapshot.reviews.some((review) => review.id === highlightedReviewId)
    ? { ...snapshot, highlighted_review_id: highlightedReviewId }
    : snapshot;
}

function parseReviewCount(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isSafeInteger(value) && value >= 0 ? value : null;
  }
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const parsed = Number(trimmed);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

function toAdminReviewedProductOption(
  row: AdminReviewedProductOptionRpcRow,
): AdminReviewedProductOption | null {
  const visibleReviewCount = parseReviewCount(row.visible_review_count);
  const totalReviewCount = parseReviewCount(row.total_review_count);
  if (
    typeof row.product_id !== 'string' ||
    row.product_id.length === 0 ||
    typeof row.product_name !== 'string' ||
    row.product_name.length === 0 ||
    visibleReviewCount === null ||
    totalReviewCount === null
  ) {
    return null;
  }
  return {
    product_id: row.product_id,
    product_name: row.product_name,
    visible_review_count: visibleReviewCount,
    total_review_count: totalReviewCount,
  };
}

function toAdminReviewRow(row: AdminReviewRpcRow): AdminProductReviewRow {
  return {
    id: row.id,
    store_id: row.store_id,
    product_id: row.product_id,
    variant_id: row.variant_id,
    order_id: row.order_id,
    order_item_id: row.order_item_id,
    product_name_snapshot: row.product_name_snapshot,
    variant_option_summary_snapshot: row.variant_option_summary_snapshot,
    rating: row.rating,
    review_text: nullableString(row.review_text),
    customer_display_name: nullableString(row.customer_display_name),
    verification_source: row.verification_source as ReviewVerificationSource,
    visibility: isReviewVisibility(row.visibility) ? row.visibility : 'visible',
    merchant_response_text: nullableString(row.merchant_response_text),
    merchant_response_by: nullableString(row.merchant_response_by),
    merchant_response_created_at: nullableString(row.merchant_response_created_at),
    merchant_response_updated_at: nullableString(row.merchant_response_updated_at),
    hidden_at: nullableString(row.hidden_at),
    hidden_by: nullableString(row.hidden_by),
    hidden_reason:
      typeof row.hidden_reason === 'string' && isReviewHiddenReason(row.hidden_reason)
        ? row.hidden_reason
        : null,
    hidden_note: nullableString(row.hidden_note),
    created_at: row.created_at,
  };
}

export async function listReviewableOrderItems(
  client: SupabaseClient,
  args: { readonly orderId?: string | null; readonly limit?: number },
): Promise<readonly ReviewableOrderItem[]> {
  const { data, error } = await client.rpc('list_customer_reviewable_order_items', {
    target_order_id: args.orderId ?? undefined,
    page_limit: normalizeLimit(args.limit, 20, 50),
  });
  if (error) throw error;
  return (data ?? []).map(toReviewableOrderItem);
}

export async function submitProductReview(
  client: SupabaseClient,
  input: SubmitProductReviewInput,
): Promise<SubmitProductReviewResult> {
  const { data, error } = await client.rpc('submit_product_review', {
    target_order_item_id: input.orderItemId,
    review_rating: input.rating,
    review_text_input: normalizeReviewText(input.reviewText) ?? undefined,
  });
  if (error) throw error;
  const row = data?.[0];
  if (!row) throw new Error('submitProductReview: empty result from submit_product_review RPC');
  return { review: toReceipt(row) };
}

export async function getCustomerProductReview(
  client: SupabaseClient,
  args: { readonly storeSlug: string; readonly productId: string },
): Promise<CustomerProductReview | null> {
  const { data, error } = await client.rpc('get_customer_product_review', {
    target_store_slug: args.storeSlug.trim(),
    target_product_id: args.productId,
  });
  if (error) throw error;
  const row = data?.[0];
  return row ? toCustomerReview(row) : null;
}

export async function updateCustomerProductReview(
  client: SupabaseClient,
  input: UpdateCustomerProductReviewInput,
): Promise<UpdateCustomerProductReviewResult> {
  const { data, error } = await client.rpc('update_customer_product_review', {
    target_review_id: input.reviewId,
    review_rating: input.rating,
    review_text_input: normalizeReviewText(input.reviewText) ?? undefined,
  });
  if (error) throw error;
  const row = data?.[0];
  if (!row) throw new Error('updateCustomerProductReview: empty result from RPC');
  return { review: toCustomerReview(row) };
}

export async function getPublicProductReviewSnapshot(
  client: SupabaseClient,
  args: {
    readonly storeSlug: string;
    readonly productId: string;
    readonly limit?: number;
    readonly highlightedReviewId?: string | null;
  },
): Promise<PublicProductReviewSnapshot> {
  const highlightedReviewId = normalizeOptionalUuid(args.highlightedReviewId) ?? null;
  const { data, error } = await client.rpc('get_public_product_review_snapshot', {
    target_store_slug: args.storeSlug.trim(),
    target_product_id: args.productId,
    review_limit: normalizeLimit(args.limit, 5, 20),
    // Supabase typegen marks defaulted RPC args optional but not nullable;
    // PostgREST still needs an explicit JSON null to select the 4-arg contract.
    highlighted_review_id: highlightedReviewId as unknown as string | undefined,
  });
  if (error) throw error;
  return toPublicProductReviewSnapshot(data, args.productId);
}

export async function listAdminReviewedProductOptions(
  client: SupabaseClient,
  args: {
    readonly storeId: string;
    readonly search?: string | null;
    readonly limit?: number;
    readonly offset?: number;
  },
): Promise<readonly AdminReviewedProductOption[]> {
  const { data, error } = await client.rpc('list_admin_reviewed_product_options', {
    target_store_id: args.storeId,
    search_term: args.search?.trim() || undefined,
    page_limit: normalizeLimit(args.limit, 100, 100),
    page_offset: normalizeOffset(args.offset),
  });
  if (error) throw error;
  return (data ?? []).flatMap((row) => toAdminReviewedProductOption(row) ?? []);
}

export async function listAdminProductReviews(
  client: SupabaseClient,
  filters: AdminReviewFilters,
): Promise<AdminReviewsPage> {
  const limit = normalizeLimit(filters.limit, 20, 50);
  const offset = normalizeOffset(filters.offset);
  const { data, error } = await client.rpc('list_admin_product_reviews', {
    target_store_id: filters.storeId,
    target_product_id: filters.productId ?? undefined,
    target_visibility: filters.visibility ?? 'all',
    page_limit: limit + 1,
    page_offset: offset,
  });
  if (error) throw error;
  const rows = (data ?? []).map(toAdminReviewRow);
  const totalCount = data?.[0]?.total_count ?? 0;
  return {
    rows: rows.slice(0, limit),
    total_count: totalCount,
    hasMore: rows.length > limit,
  };
}

export async function upsertProductReviewResponse(
  client: SupabaseClient,
  input: UpsertReviewResponseInput,
): Promise<void> {
  const { error } = await client.rpc('upsert_product_review_response', {
    target_review_id: input.reviewId,
    response_text: input.responseText.trim(),
  });
  if (error) throw error;
}

export async function removeProductReviewResponse(
  client: SupabaseClient,
  input: RemoveReviewResponseInput,
): Promise<void> {
  const { error } = await client.rpc('remove_product_review_response', {
    target_review_id: input.reviewId,
  });
  if (error) throw error;
}

export async function setProductReviewVisibility(
  client: SupabaseClient,
  input: SetReviewVisibilityInput,
): Promise<void> {
  const { error } = await client.rpc('set_product_review_visibility', {
    target_review_id: input.reviewId,
    target_visibility: input.visibility,
    target_hidden_reason:
      input.visibility === 'hidden' ? (input.hiddenReason ?? undefined) : undefined,
    target_hidden_note: input.visibility === 'hidden' ? (input.hiddenNote ?? undefined) : undefined,
  });
  if (error) throw error;
}

export const reviewService: ReviewService = {
  listReviewableOrderItems,
  submitProductReview,
  getCustomerProductReview,
  updateCustomerProductReview,
  getPublicProductReviewSnapshot,
  listAdminReviewedProductOptions,
  listAdminProductReviews,
  upsertProductReviewResponse,
  removeProductReviewResponse,
  setProductReviewVisibility,
};

export function mapReviewError(err: unknown): ReviewErrorKey {
  if (!err || typeof err !== 'object') return 'common.errors.unknown';
  if (err instanceof TypeError && /fetch/i.test(err.message)) return 'common.errors.networkFailed';

  const maybe = err as { code?: string; message?: string; name?: string };
  if (maybe.name === 'AuthRetryableFetchError') return 'common.errors.networkFailed';
  if (maybe.message && /network|fetch|abort/i.test(maybe.message)) {
    return 'common.errors.networkFailed';
  }

  switch (maybe.code) {
    case '42501':
      return 'reviews.errors.permissionDenied';
    case 'P0571':
      return 'reviews.errors.notEligible';
    case 'P0572':
    case '23505':
      return 'reviews.errors.alreadyReviewed';
    case 'P0573':
      return 'reviews.errors.productUnavailable';
    case 'P0574':
      return 'reviews.errors.ratingInvalid';
    case 'P0575':
      return 'reviews.errors.whitespaceOnly';
    case 'P0578':
      return 'reviews.errors.textTooLong';
    default:
      return 'common.errors.unknown';
  }
}
